import { Device, Station } from '@mi-front/core';
import { useMemo } from 'react';
import { useStreamerBoxDevices } from './useStreamerBoxDevices';

/**
 * Hook to get the streamer box device for a specific station.
 * Returns the device if found, null if not found or no deviceId configured,
 * and the loading state.
 * isLoading is true only when station has deviceId and device is not loaded yet.
 * If no deviceId, isLoading is false even if parent devices are loading.
 */
export function useStreamerBoxDeviceForStation(station: Station): {
  device: Device | null;
  isLoading: boolean;
} {
  const { data: devices, isLoading: devicesLoading } = useStreamerBoxDevices();
  const deviceId = station.featuresConfig?.streamerBox?.deviceId;

  const device = useMemo(() => {
    // Return null if no deviceId is configured for this station
    if (!deviceId) {
      return null;
    }

    // Return null if devices haven't loaded yet
    if (!devices) {
      return null;
    }

    // Convert deviceId string to number and find matching device
    const deviceIdNumber = Number(deviceId);
    const foundDevice = devices.find((d) => d.id === deviceIdNumber);

    return foundDevice ?? null;
  }, [devices, deviceId]);

  // isLoading is true only if station has deviceId and devices are still loading
  // If no deviceId, always return false even if parent query is loading
  const isLoading = deviceId ? devicesLoading : false;

  return { device, isLoading };
}
